<template>
  <div class="configuracao-avancada-page">
    <!-- PageHeader com botão voltar -->
    <PageHeader
      :title="t('Configurações Avançadas')"
      :subtitle="t('Gerencie metas financeiras e configurações operacionais')"
      icon="pi pi-cog">
      <template #actions>
        <Button
          data-testid="franquias-config-avancada-back-button"
          icon="pi pi-arrow-left"
          :label="t('Voltar')"
          severity="contrast"
          class="back-button"
          @click="handleBack"
        />
      </template>
    </PageHeader>

    <!-- Tabs -->
    <Tabs v-model:value="activeTabIndex">
      <TabList>
        <Tab :value="0">{{ t('Metas Financeiras') }}</Tab>
        <Tab :value="1">{{ t('Configurações Operacionais') }}</Tab>
      </TabList>

      <TabPanels>
        <!-- Metas Financeiras -->
        <TabPanel :value="0">
          <FranquiasMetasTab
            v-if="franquiaId"
            :franquia-id="franquiaId"
            :franquia-data="franquiaData || {}" />
        </TabPanel>

        <!-- Configurações Operacionais -->
        <TabPanel :value="1">
          <FranquiasOperacionalTab
            v-if="franquiaId"
            :franquia-id="franquiaId"
            :franquia-data="franquiaData || {}" />
        </TabPanel>
      </TabPanels>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useFranquias } from '../../composables/useFranquias';
import { log } from '../../utils/DebugLogger';
import PageHeader from '../../ui/PageHeader.vue';

// Import components
import FranquiasMetasTab from './FranquiasMetasTab.vue';
import FranquiasOperacionalTab from './FranquiasOperacionalTab.vue';

interface FranquiaData {
  [key: string]: unknown;
}

const { t } = useI18n();
const { fetchFranquia } = useFranquias();
const route = useRoute();
const router = useRouter();

// Estado
const franquiaId = ref<number | undefined>(undefined);
const franquiaData = ref<FranquiaData | null>(null);
const activeTabIndex = ref<number>(0);

// Carregar dados da franquia
const loadFranquiaData = async (): Promise<void> => {
  try {
    log('FranquiasConfiguracaoAvancadaPage', 'loadFranquiaData', 'Carregando dados da franquia', franquiaId.value);
    if (franquiaId.value === undefined) {
      log('FranquiasConfiguracaoAvancadaPage', 'loadFranquiaData', 'No franquiaId — skipping fetch');
      return;
    }
    const data = await fetchFranquia(franquiaId.value) as Record<string, unknown>;
    log('FranquiasConfiguracaoAvancadaPage', 'loadFranquiaData', 'Resposta da API', data);

    if (data) {
      franquiaData.value = data as FranquiaData;
      log('FranquiasConfiguracaoAvancadaPage', 'loadFranquiaData', 'Dados da franquia carregados', franquiaData.value);
    } else {
      log('FranquiasConfiguracaoAvancadaPage', 'loadFranquiaData', 'Resposta sem dados', data);
      franquiaData.value = {};
    }
  } catch (err) {
    log('FranquiasConfiguracaoAvancadaPage', 'loadFranquiaData', 'Erro ao carregar dados da franquia', err);
    eventBus.emit('globalMessage.error', t('Erro ao carregar dados da franquia'));
    franquiaData.value = {};
  }
};

// Voltar para lista
const handleBack = (): void => {
  router.push('/franquias');
};

// Refresh dados
const handleRefresh = async (): Promise<void> => {
  await loadFranquiaData();
};

onMounted(() => {
  const idParam = route.params.id;
  franquiaId.value = parseInt(Array.isArray(idParam) ? idParam[0] : (idParam as string));
  log('FranquiasConfiguracaoAvancadaPage', 'onMounted', 'mounted', franquiaId.value);
  if (franquiaId.value) {
    loadFranquiaData();
  }

  eventBus.on('franquias.refresh-single', handleRefresh);
});

onBeforeUnmount(() => {
  eventBus.off('franquias.refresh-single', handleRefresh);
});
</script>

<style scoped>
.configuracao-avancada-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.back-button {
  background: linear-gradient(
    135deg,
    var(--tenant-secondary-color),
    var(--tenant-secondary-hover-color, var(--tenant-secondary-color))
  ) !important;
  border: 1px solid white !important;
  color: white !important;
}

.back-button:hover {
  opacity: 0.9;
}
</style>
